import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getPublicAuthorPhotoUrl, getPublicCoverUrl, getPublicInstitutionalContentUrl, publicAPI } from "@/services/api";
import { PRODUCT_TYPES, formatCurrency } from "@/utils/constants";
import { getProductCategoryLabel } from "@/utils/productCategories";
import { ArrowLeft, ShoppingCart } from "lucide-react";
import articoLogo from "@/assets/artico-logo.png";

export default function PublicCataloguePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedBook, setSelectedBook] = useState(null);

  const loadProducts = useCallback(async () => {
    try {
      const res = await publicAPI.getCatalogue();
      setProducts(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const applyFilters = useCallback(() => {
    let f = [...products];
    if (filter !== "all") f = f.filter((p) => p.product_type === filter);
    if (search) {
      const term = search.toLowerCase();
      f = f.filter((p) => `${p.title} ${p.description} ${p.author_name || ""} ${p.category || ""} ${p.subcategory || ""}`.toLowerCase().includes(term));
    }
    setFiltered(f);
  }, [products, filter, search]);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { applyFilters(); }, [applyFilters]);
  useEffect(() => {
    const requestedProductId = searchParams.get("product");
    if (!requestedProductId || products.length === 0 || selectedBook) return;
    const product = products.find((item) => item.id === requestedProductId);
    if (product) setSelectedBook(product);
  }, [products, searchParams, selectedBook]);

  const closeSelectedBook = () => {
    setSelectedBook(null);
    if (searchParams.has("product")) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("product");
      setSearchParams(nextParams, { replace: true });
    }
  };

  if (loading) return <div className="min-h-screen bg-[#FCFBF8] flex items-center justify-center">Chargement...</div>;

  return (
    <div className="min-h-screen bg-[#FCFBF8]">
      {/* Header */}
      <header className="backdrop-blur-xl bg-[#FCFBF8]/80 border-b border-[#E6E4E0]/50 sticky top-0 z-50">
        <div className="container mx-auto flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex items-center justify-between gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-[#5C625F]">
                <ArrowLeft size={18} className="mr-1" /> Accueil
              </Button>
            </Link>
            <div className="flex items-center gap-4">
              <img src={articoLogo} alt="ARTICO SARLU" className="h-14 w-14 rounded-xl object-contain" />
              <h1 className="text-xl font-extrabold text-[#2A4B46] font-['Outfit']">Catalogue ARTICO</h1>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-3">
            <Link to="/login">
              <Button variant="ghost" className="w-full text-[#2A4B46]">Connexion</Button>
            </Link>
            <Link to="/register">
              <Button className="w-full rounded-full bg-[#2A4B46] text-white hover:bg-[#1f3a36]">S'inscrire</Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 sm:px-6 sm:py-10">
        <div className="flex flex-wrap items-center gap-4">
          <img src={articoLogo} alt="ARTICO SARLU" className="h-16 w-16 rounded-xl object-contain" />
          <h2 className="text-2xl font-extrabold text-[#2A4B46] font-['Outfit'] sm:text-3xl">Catalogue ARTICO</h2>
        </div>
        <p className="text-[#5C625F] mb-8">Parcourez nos oeuvres de qualite</p>

        {/* Filters */}
        <div className="mb-8 grid gap-3 md:grid-cols-[minmax(0,1fr)_240px]">
          <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="public-search" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="public-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              <SelectItem value="texte_simple">Documents textes</SelectItem>
              <SelectItem value="texte_sonorise">Fichier audio</SelectItem>
              <SelectItem value="texte_chante">Fichier video</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {filtered.length === 0 ? (
          <Card className="border-[#E6E4E0]">
            <CardContent className="py-12 text-center text-[#5C625F]">Aucun produit trouve</CardContent>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
            {filtered.map((product) => (
              <Card key={product.id} className="border-[#E6E4E0] shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-testid={`public-product-${product.id}`}>
                <button type="button" onClick={() => setSelectedBook(product)} className="relative flex aspect-[4/5] w-full items-center justify-center overflow-hidden bg-[#263A36]">
                  {(product.thumbnail_path || product.cover_image_path) ? (
                    <img src={getPublicCoverUrl(product.thumbnail_path || product.cover_image_path)} alt={product.title} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 hover:scale-105" />
                  ) : (
                    <span className="text-2xl font-bold text-white/85">{product.product_type === "texte_simple" ? "Livre" : product.product_type === "texte_sonorise" ? "Audio" : "Video"}</span>
                  )}
                  <AuthorAvatar url={product.author_profile_photo_path ? getPublicAuthorPhotoUrl(product.author_profile_photo_path) : null} name={product.author_name} />
                </button>
                <CardHeader className="p-3 pb-2">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <CardTitle className="line-clamp-2 text-sm font-semibold text-[#2A4B46] font-['Outfit']">{product.title}</CardTitle>
                    <Badge className="shrink-0 bg-[#D4A373] px-2 py-0.5 text-[11px] text-white">{getProductAccessLabel(product)}</Badge>
                  </div>
                  <Badge className="w-fit bg-[#F3EFE9] text-[11px] text-[#2A4B46]">{PRODUCT_TYPES[product.product_type]?.label}</Badge>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <p className="mb-3 line-clamp-2 text-xs leading-5 text-[#5C625F]">{product.description}</p>
                  <p className="mb-3 text-xs font-semibold text-[#6D7470]">Auteur : {product.author_name || "ARTICO"}</p>
                  {isPublicInstitutionalProduct(product) ? (
                    <a href={getPublicInstitutionalContentUrl(product.id)} target="_blank" rel="noreferrer">
                      <Button className="w-full bg-[#2A4B46] hover:bg-[#1f3a36] text-white" data-testid={`public-read-${product.id}`}>
                        Lire gratuitement
                      </Button>
                    </a>
                  ) : product.institutional_catalogue ? (
                    <Link to="/login">
                      <Button className="w-full bg-[#2A4B46] hover:bg-[#1f3a36] text-white" data-testid={`public-access-${product.id}`}>
                        {getInstitutionalLoginLabel(product)}
                      </Button>
                    </Link>
                  ) : (
                    <Link to="/register">
                      <Button className="w-full bg-[#2A4B46] hover:bg-[#1f3a36] text-white" data-testid={`public-buy-${product.id}`}>
                        <ShoppingCart size={18} className="mr-2" /> S'inscrire pour acheter
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        <PublicBookDetailsDialog product={selectedBook} open={!!selectedBook} onOpenChange={(open) => !open && closeSelectedBook()} />
      </div>
    </div>
  );
}

function AuthorAvatar({ url, name }) {
  return (
    <div className="absolute bottom-2 right-2 z-20 flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-[#2A4B46] text-[10px] font-bold text-white shadow-lg">
      {url ? <img src={url} alt={`Photo de ${name || "auteur"}`} className="h-full w-full object-cover" loading="lazy" /> : getAuthorInitials(name)}
    </div>
  );
}

function getAuthorInitials(name = "") {
  const parts = String(name || "A").trim().split(/\s+/).filter(Boolean);
  return `${parts[0]?.[0] || "A"}${parts[1]?.[0] || ""}`.toUpperCase();
}

function PublicBookDetailsDialog({ product, open, onOpenChange }) {
  if (!product) return null;
  const coverPath = product.cover_image_path || product.thumbnail_path;
  const coverUrl = coverPath ? getPublicCoverUrl(coverPath) : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-4xl overflow-hidden border-0 bg-[#FCFBF8] p-0 shadow-2xl sm:rounded-2xl">
        <div className="grid max-h-[90vh] overflow-y-auto md:grid-cols-[320px_minmax(0,1fr)]">
          <div className="bg-[#263A36] p-6">
            <div className="aspect-[3/4] overflow-hidden rounded-lg bg-[#F3EFE9] shadow-2xl">
              {coverUrl ? <img src={coverUrl} alt={product.title} loading="eager" decoding="async" fetchPriority="high" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center p-5 text-center text-2xl font-bold text-[#2A4B46]">{product.title}</div>}
            </div>
          </div>
          <div className="space-y-5 p-6">
            <div>
              <Badge className="mb-3 bg-[#D4A373] text-white">{getProductAccessLabel(product)}</Badge>
              <h2 className="text-3xl font-extrabold text-[#2A4B46] font-['Outfit']">{product.title}</h2>
              <p className="mt-2 text-sm text-[#5C625F]">{PRODUCT_TYPES[product.product_type]?.label}</p>
              <p className="mt-1 text-sm font-semibold text-[#2A4B46]">Auteur : {product.author_name || "ARTICO"}</p>
              <p className="mt-1 text-sm text-[#5C625F]">
                {getProductCategoryLabel(product.category)}{product.subcategory ? ` / ${product.subcategory}` : ""}
              </p>
            </div>
            <section>
              <h3 className="text-sm font-bold uppercase tracking-wide text-[#2A4B46]">Description</h3>
              <p className="mt-2 whitespace-pre-wrap leading-7 text-[#3D4542]">{product.summary || product.description || "Aucune description."}</p>
            </section>
            <section>
              <h3 className="text-sm font-bold uppercase tracking-wide text-[#2A4B46]">A propos de l'auteur</h3>
              <p className="mt-2 whitespace-pre-wrap leading-7 text-[#3D4542]">{product.author_about || "Information auteur non renseignee."}</p>
            </section>
            {isPublicInstitutionalProduct(product) ? (
              <a href={getPublicInstitutionalContentUrl(product.id)} target="_blank" rel="noreferrer">
                <Button className="bg-[#2A4B46] text-white hover:bg-[#1f3a36]">Lire gratuitement</Button>
              </a>
            ) : product.institutional_catalogue ? (
              <Link to="/login">
                <Button className="bg-[#2A4B46] text-white hover:bg-[#1f3a36]">{getInstitutionalLoginLabel(product)}</Button>
              </Link>
            ) : (
              <Link to="/register">
                <Button className="bg-[#2A4B46] text-white hover:bg-[#1f3a36]">S'inscrire pour acceder</Button>
              </Link>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function isPublicInstitutionalProduct(product) {
  return Boolean(product?.institutional_catalogue && product?.institutional_access_scope === "public");
}

function getProductAccessLabel(product) {
  if (!product?.institutional_catalogue) {
    return product?.is_free_for_active_par || Number(product?.price_mc || 0) <= 0 ? "Gratuit" : formatCurrency(product?.price_mc);
  }
  const labels = {
    public_domain: "Domaine public",
    creative_commons: "Licence Creative Commons",
    authorized_distribution: "Diffusé avec autorisation",
  };
  return labels[product.rights_basis] || "Accès gratuit";
}

function getInstitutionalLoginLabel(product) {
  return product?.institutional_access_scope === "active_par" ? "Accès PAR actif" : "Se connecter pour lire";
}
